import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import './styles.css'
import { Button } from 'primereact/button'
import { PersonApi, PersonRegisterApi, PersonRegisterDto } from '../../api'
import { InputText } from 'primereact/inputtext'
import { Calendar } from 'primereact/calendar'
import { Dropdown } from 'primereact/dropdown'
import { Loading } from '../loading/Loading'
import { authState } from '../../state/Auth.state'
import { appState } from '../../state/App.state'

const NULL_DATE = new Date('1800-01-01')
const EMPTY_PERSON_REGISTER: PersonRegisterDto = {
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    country: 'DE',
    zipCode: '',
    city: '',
    street: '',
    birthday: NULL_DATE,
    membershipName: '',
    remark: ''
}

const membershipOptions = [
    { label: 'IGV', value: 'IGV' },
    { label: 'PFAE', value: 'PFAE' },
    { label: 'MFT', value: 'MFT' }
]

export const Register = () => {
/* STATE */
const [loading, setLoading] = useState<boolean>(false)
const [form, setForm] = useState<PersonRegisterDto>(EMPTY_PERSON_REGISTER)
const [showInfo, setShowInfo] = useState(false)
const [countries, setCountries] = useState<string[]>([])

/* EFFECTS */
useEffect(() => {
    new PersonApi(authState.apicfg())
        .apiPersonCountriesGet()
        .then(setCountries)
        .catch(() => {})
}, [])

    /* DERIVED STATE */
    const formValid = useMemo(() => {
        if (!form?.firstName || form?.firstName?.length < 1) return false
        if (!form?.lastName || form?.lastName?.length < 1) return false

        if (!form?.email || form?.email?.length < 1) return false
        if (!form?.phoneNumber || form?.phoneNumber?.length < 1) return false

        if (!form?.birthday || form?.birthday <= NULL_DATE) return false

        if (!form?.country || form?.country?.length < 1) return false
        if (!form?.zipCode || form?.zipCode?.length < 1) return false
        if (!form?.city || form?.city?.length < 1) return false
        if (!form?.street || form?.street?.length < 1) return false

        return true
    }, [form])

    /* LOGIC */
    const register = useCallback((event?: React.SyntheticEvent) => {
        setLoading(true)
        new PersonRegisterApi(authState.apicfg())
            .apiPersonRegisterPost({
                personRegisterDto: form
            })
            .then(() => {
                //appState.addToast('Registrierung erfolgreich!', 'success')
                setForm(EMPTY_PERSON_REGISTER)
                setShowInfo(true)
            })
            .catch((err) => {
                appState.addToast('Registrierung fehlgeschlagen!', 'error')
                console.error(err)
            })
            .finally(() => setLoading(false))
    }, [form])

    /* TEMPLATE */
    return (
        <>
            <Loading loading={loading} />
            <div className='register-container'>
                <div className='register-form card' style={{ position: 'relative' }}>
                    <h2 style={{ marginBottom: '1rem' }}>Registrieren</h2>
                    {showInfo && (
                        <div
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'rgba(255,255,255,0.95)',
                                zIndex: 10,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '2rem'
                            }}
                        >
                            <div style={{ marginBottom: '1.5rem', color: 'green', fontWeight: 'bold', fontSize: '1.1rem', textAlign: 'center' }}>
                                Registrierung erfolgreich!<br />
                                Bitte prüfen Sie Ihr E-Mail-Postfach für weitere Informationen.<br />
                                Bei Fragen wenden Sie sich bitte an den Support.
                            </div>
                            <Button onClick={() => setShowInfo(false)}>OK</Button>
                        </div>
                    )}
                    <form
                        onSubmit={(e) => {
                            e.preventDefault()
                            register(e)
                        }}
                        style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
                    >
                        {/* first and lastname */}
                        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                            <InputText
                                style={{ flex: 1 }}
                                placeholder='Vorname *'
                                value={form.firstName}
                                onChange={(e) => setForm((v) => ({ ...v, firstName: e.target.value ?? '' }))}
                            />
                            <InputText
                                style={{ flex: 1 }}
                                placeholder='Nachname *'
                                value={form.lastName}
                                onChange={(e) => setForm((v) => ({ ...v, lastName: e.target.value ?? '' }))}
                            />
                        </div>
                        {/* email and phonenumber */}
                        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                            <InputText
                                style={{ flex: 1 }}
                                placeholder='E-Mail *'
                                value={form.email}
                                onChange={(e) => setForm((v) => ({ ...v, email: e.target.value ?? '' }))}
                            />
                        </div>
                        {/* birthday */}
                        <div style={{ display: 'flex' }}>
                            <Calendar
                                style={{ flex: 1 }}
                                placeholder='Geburtstag *'
                                value={(form?.birthday ?? new Date(0)) > new Date(0) ? form.birthday : null}
                                dateFormat='dd.mm.yy'
                                onChange={(e) => setForm((v) => ({ ...v, birthday: e.value ?? new Date(0) }))}
                                showIcon
                                showButtonBar
                            />
                            <InputText
                                style={{ flex: 1 }}
                                placeholder='Tel. / Mobil *'
                                value={form.phoneNumber}
                                onChange={(e) => setForm((v) => ({ ...v, phoneNumber: e.target.value ?? '' }))}
                            />
                        </div>
                        {/* country, zip, city */}
                        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                            <Dropdown
                                style={{ flex: 1 }}
                                placeholder='Land *'
                                value={form.country}
                                options={countries}
                                onChange={(e) => setForm((v) => ({ ...v, country: e.value ?? '' }))}
                                filter
                                showClear
                                editable
                            />
                            <InputText
                                style={{ flex: 1 }}
                                placeholder='PLZ *'
                                value={form.zipCode}
                                onChange={(e) => setForm((v) => ({ ...v, zipCode: e.target.value ?? '' }))}
                            />
                        </div>
                        {/* street */}
                        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                            <InputText
                                style={{ flex: 1 }}
                                placeholder='Stadt *'
                                value={form.city}
                                onChange={(e) => setForm((v) => ({ ...v, city: e.target.value ?? '' }))}
                            />
                            <InputText
                                style={{ flex: 1 }}
                                placeholder='Straße *'
                                value={form.street}
                                onChange={(e) => setForm((v) => ({ ...v, street: e.target.value ?? '' }))}
                            />
                        </div>
                        {/* membership name as dropdown */}
                        <div style={{ display: 'flex' }}>
                            <Dropdown
                                style={{ flex: 1 }}
                                placeholder='Verein auswählen'
                                value={form.membershipName}
                                options={membershipOptions}
                                onChange={(e) => setForm((v) => ({ ...v, membershipName: e.value ?? '' }))}
                                showClear
                            />
                        </div>
                        {/* remark */}
                        <div style={{ display: 'flex' }}>
                            <InputText
                                style={{ flex: 1 }}
                                placeholder='Anmerkung'
                                value={form.remark}
                                onChange={(e) => setForm((v) => ({ ...v, remark: e.target.value ?? '' }))}
                            />
                        </div>
                        <Button disabled={!formValid} type="submit">
                            Registrieren
                        </Button>
                        <br />
                        <Button outlined onClick={() => (window.location.hash = '#/home')}>
                            Zurück zum Login
                        </Button>
                    </form>
                </div>
            </div>
        </>
    )
}